import type { CurrentUser, UpdateMeRequest } from '@ralph/contracts'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { api } from '@/api/client.ts'
import { queryKeys, useSessionSnapshot } from '@/hooks/use-api.ts'
import {
  ANONYMOUS,
  clearUnsentOwner,
  mayWriteChoiceTo,
  readUnsentOwner,
  shouldAdoptAccountLocale,
  type UnsentChoice,
  writeUnsentOwner
} from '@/i18n/account-rules.ts'
import { useI18n } from '@/i18n/I18nProvider.tsx'
import type { Locale } from '@/i18n/locale.ts'

/**
 * Keeps the language a person is reading in and the language on their account in step
 * (PRD-27 LOC-003, D-35).
 *
 * ── Which copy is the truth ──
 *
 * `users.locale` is, once somebody has chosen it; localStorage is only its pre-paint mirror —
 * `boot.js` cannot await a session, so it needs something on disk to stamp the document with
 * before the first frame. That split is what makes a choice follow a person to a second
 * device instead of being a property of one browser profile.
 *
 * ── Why both directions are EDGE-triggered, and why that is the whole design ──
 *
 * The first cut was level-triggered: two effects, each watching `tab !== account`. One pulled
 * the account down into the tab, the other pushed the tab up into the account. A click made
 * that condition true for BOTH in the same commit, each concluded the other side was stale,
 * and they chased one another across every server round trip — ~70 flips a second, ending on
 * whichever request landed last. A difference between two values cannot say who moved, so no
 * amount of guarding a comparison fixes it. The intent has to be carried explicitly:
 *
 *   UP    fires on a CHOICE — the provider raises one only when a person presses the switcher
 *         (`setLocale`), never when a language merely arrives (`adoptLocale`).
 *   DOWN  fires on a CHANGE in what the account says — first sight at sign-in, or another
 *         device's choice arriving — never on the tab differing from it.
 *
 * Neither reacts to the other's output: adopting raises no choice, and a confirmed write
 * changes the account to what the tab already shows. The loop is not guarded against; it has
 * no edge to travel along.
 *
 * ── What is deliberately NOT done ──
 *
 * An inference is never written up. A browser asking for Portuguese is a guess about a
 * person, and recording it as their preference would make a guess follow them everywhere.
 *
 * A failed write is silent and is not retried on a timer: the person has the language they
 * asked for, the unsent marker survives a reload, and the next sign-in or the next choice
 * carries it up. A toast about a preference is noise about a problem nobody can act on.
 *
 * The session is OBSERVED here and never fetched (`useSessionSnapshot`). This component sits
 * above the router, so a fetch of its own would put a session read in flight on the sign-in
 * page — which is how the first cut also managed to bounce people who had just signed in
 * straight back to `/login` (see `useLogin`).
 */
export function useLocaleAccountSync(): void {
  const { locale, choice, adoptLocale } = useI18n()
  const session = useSessionSnapshot()
  const client = useQueryClient()

  const user = session.data?.authenticated ? (session.data.user ?? null) : null
  const userId = user?.id ?? null
  const accountLocale = user?.locale ?? null
  const accountChose = user?.localeChosen === true

  // An explicit choice that no account has confirmed yet. It survives a reload — and an OIDC
  // round trip, which IS a reload — because the choice made on the sign-in page has to still
  // be here when the person comes back with a session.
  const [unsent, setUnsent] = useState<UnsentChoice | null>(() => {
    const owner = readUnsentOwner()
    return owner === null ? null : { owner, locale, seq: 0 }
  })

  // Latest-value refs, declared FIRST: effects run in declaration order, so the two below read
  // this render's values rather than the previous one's. They exist so DOWN can consult the
  // tab's language and the unsent choice without DEPENDING on them — a dependency would make
  // it level-triggered again, which is the bug.
  const tabLocale = useRef(locale)
  const unsentNow = useRef(unsent)
  useEffect(() => {
    tabLocale.current = locale
    unsentNow.current = unsent
  })

  // ---- a choice becomes an unsent choice -------------------------------------------------
  const recorded = useRef(0)
  useEffect(() => {
    if (choice === null || choice.seq === recorded.current) return
    recorded.current = choice.seq
    const next: UnsentChoice = { owner: userId ?? ANONYMOUS, locale: choice.locale, seq: choice.seq }
    writeUnsentOwner(next.owner)
    unsentNow.current = next
    setUnsent(next)
  }, [choice, userId])

  // ---- UP: an unsent choice goes to the account ------------------------------------------
  const sent = useRef<number | null>(null)
  const { mutate } = useMutation({
    // Serial. Two quick presses are two writes, and they must land in the order they were
    // made or the account ends on the first language while the tab shows the second.
    scope: { id: 'ralph.locale' },
    mutationFn: (input: { locale: Locale; seq: number }) =>
      api<{ user: CurrentUser }>('/api/me', {
        method: 'PATCH',
        body: JSON.stringify({ locale: input.locale } satisfies UpdateMeRequest)
      }),
    onSuccess: (result, input) => {
      // Seed the cached session rather than invalidating it: the response already carries the
      // new projection, and a refetch would make every switch cost a second round trip.
      client.setQueryData(queryKeys.session, (current: unknown) =>
        current && typeof current === 'object' ? { ...current, user: result.user } : current
      )
      // Settle only the choice this write carried. A newer one may already be waiting behind
      // it, and clearing that would leave DOWN free to adopt the language just overruled.
      if (unsentNow.current?.seq !== input.seq) return
      clearUnsentOwner()
      unsentNow.current = null
      setUnsent(null)
    },
    onError: (_error, input) => {
      // Let a later trigger try again; the marker on disk is what remembers until then.
      if (sent.current === input.seq) sent.current = null
    }
  })

  useEffect(() => {
    if (userId === null || unsent === null) return
    if (!mayWriteChoiceTo(unsent.owner, userId)) {
      clearUnsentOwner()
      unsentNow.current = null
      setUnsent(null)
      return
    }
    if (sent.current === unsent.seq) return
    sent.current = unsent.seq
    mutate({ locale: unsent.locale, seq: unsent.seq })
  }, [userId, unsent, mutate])

  // ---- DOWN: the account's language arrives ----------------------------------------------
  const reconciled = useRef<string | null>(null)
  useEffect(() => {
    if (userId === null || accountLocale === null) {
      reconciled.current = null
      return
    }
    // Edge-triggered: act once per distinct thing the account has said, and never again until
    // it says something else. This is the line that makes the loop impossible rather than
    // unlikely — a re-render, a click, a settled write all arrive here and leave at once.
    const stamp = `${userId}|${accountLocale}|${accountChose}`
    if (reconciled.current === stamp) return
    reconciled.current = stamp

    const adopt = shouldAdoptAccountLocale({
      accountLocale,
      accountChose,
      tabLocale: tabLocale.current,
      hasUnsentChoice: unsentNow.current !== null
    })
    if (adopt) adoptLocale(accountLocale)
  }, [userId, accountLocale, accountChose, adoptLocale])
}

/**
 * Mount point for {@link useLocaleAccountSync}, rendering nothing.
 *
 * A component rather than a hook call inside `I18nProvider`, so the provider itself keeps no
 * dependency on the query client or the API — a test, a storybook mount or a future surface
 * can render the provider alone and still get words and formatters.
 */
export function LocaleAccountSync(): null {
  useLocaleAccountSync()
  return null
}
